<template>
  <main class="slot-selection" id="mainContent">
    <Stepper :step="stepNum" />
    <div class="app-main-content">
      <h1>{{ screenData.title }}</h1>
      <p>{{ screenData.description }}</p>
      <SlotFilters />
      <SlotAlert />
      <SlotDays @doNext="handleEnableNext" />
      <BaseButtonGroup>
        <!-- either [Previous] (create) or [Back to View reservations] (update) -->
        <button v-if="isModify && !checkOnlineToken" type="button" @click="handleRouting('list')">
          {{ language.btnResList }}
        </button>
        <button v-else type="button" @click="handleRouting('prev')">
          {{ language.btnPrev }}
        </button>
        <!-- [Next] is always there -->
        <button type="button" class="app-btn-primary" :class="{ 'disabled': !enableNext }" :aria-disabled="!enableNext"
                @click="handleRouting('next')">
          {{ language.btnNext }}
        </button>
        <!-- conditional [Back to Reservations home] (update) -->
        <button v-if="isModify && !checkOnlineToken" type="button" class="btn-home" @click.prevent="handleRouting('home')">
          {{ language.btnResHome }}
        </button>
      </BaseButtonGroup>
    </div>
  </main>
</template>


<script setup>
import { ref, computed, watch, onBeforeMount, onMounted, onUpdated } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import appConfig from '@/app.config';
import Stepper from '@/components/reservation/Stepper';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import { SLOTS_HOLD_CREATE } from '../store/_commons';
import SlotDays from '../components/reservation/slots/SlotDays.vue';
import SlotFilters from '../components/reservation/slots/SlotFilters.vue';
import SlotAlert from '../components/reservation/slots/SlotAlert.vue';
import { useHead } from '@unhead/vue'

// Use usehead to manage the page meta
useHead({
  title: `Reservation date and time | ${appConfig.shortName}`,
  meta: [
    {
      name: 'description',
      content: 'Select a date and time for your service. Reservations are added throughout the week.'
    }
  ]
})

// Vuex store and router
const store = useStore();
const router = useRouter();

// Reactive State
const stepNum = ref(2);
const screenData = ref({});
const enableNext = ref(false);
const isModify = ref(false);

// Actions
const createSlotHold = (payload) => store.dispatch(SLOTS_HOLD_CREATE, payload);

// Computed properties
const LANG = computed(() => store.state.user?.LANG);
const PAGES = computed(() => store.state.user?.PAGES);
const STATUSES = computed(() => store.state.user?.STATUSES);
const STEPS = computed(() => store.state.user?.STEPS);
const availableSlotsByVisitType = computed(() => store.state.user?.availableSlotsByVisitType);
const resSlot = computed(() => store.state.user?.resSlot);
const resType = computed(() => store.state.user?.resType);
const networkResponse = computed(() => store.state.user?.networkResponse);
const reservationSlotId = computed(() => store.state.user?.reservationSlotId);
const resSelectedForUpdate = computed(() => store.state.user?.resSelectedForUpdate);
const language = computed(() => LANG.value);

const checkOnlineToken = computed(() => {
  const token = store.state.user?.onlineToken;
  return token != '' && token != null && token != undefined;
});

// Watchers
watch(reservationSlotId, (newId) => {
  if (networkResponse.value.status === STATUSES.value.SUCCESS && newId > 0) {
    router.push({ name: PAGES.value.userInfo });
  }
});

watch(resSelectedForUpdate, () => {
  isModify.value = updateInProgress();
});

// Lifecycle Hooks
onBeforeMount(() => {
  if (resType.value < 0) {
    router.push({ name: PAGES.value.selectType });
  }
  screenData.value = STEPS.value[stepNum.value];
});

onMounted(() => {
  enableNextStep();
  isModify.value = updateInProgress();
});

onUpdated(() => {
  enableNextStep();
});

// Methods
const enableNextStep = () => {
  enableNext.value = (availableSlotsByVisitType.value.length > 0) && (resSlot.value > 0);
};

const handleEnableNext = (value) => {
  enableNext.value = value;
};

/**
 * Routing based on Previous/Next (create), Return to View reservations/Next (update) sets of buttons,
 * with an optional Return to Reservations home (update) button.
 * @param target Desired router destination.
 */
const handleRouting = (target) => {
  switch (target) {
    case 'prev':
      if(store.state.user.resSubStep > 0) {
        router.push({ name: PAGES.value.selectTypeOptions });
      } else {
        router.push({ name: PAGES.value.selectType });
      }
      break;
    case 'next':
      // do nothing is [Next] is not enabled (use aria-disabled for better accessibility)
      if (enableNext.value) {
        createSlotHold(false);
      }
      break;
    case 'list':
      router.push({ name: PAGES.value.updateRes });
      break;
    case 'home':
      router.push({ name: PAGES.value.home });
      break;
    default:
      // nada
  }
};

const updateInProgress = () => {
  // Use Object.keys() to check if the object is empty.
  const isNotEmpty = Object.keys(resSelectedForUpdate.value).length > 0;

  return isNotEmpty
    && resSelectedForUpdate.value.visitTypeId
    && resSelectedForUpdate.value.visitTypeId > 0;
};
</script>


<style lang="scss" scoped>
.slot-selection {
  .btn-home {
    display: block; // one-off Home button
  }
}

.app-button-group {
  button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}
</style>
